import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './layouts/AdminLayout';
import PublicLayout from './layouts/PublicLayout';
import Dashboard from './pages/Dashboard';
import IntakeGroups from './pages/IntakeGroups';
import Participants from './pages/Participants';
import Finances from './pages/Finances';
import Tenants from './pages/Tenants';
import TenantUsers from './pages/TenantUsers';
import SuperSettings from './pages/SuperSettings';
import RegistrationForm from './pages/RegistrationForm';
import EmailTemplates from './pages/EmailTemplates';
import EmailLogs from './pages/EmailLogs';
import Login from './pages/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';

function LoadingScreen({ message = 'Starting Sportiko...' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="flex flex-col items-center gap-6">
        <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
        <div className="text-indigo-600 font-black text-[10px] uppercase tracking-[0.4em] animate-pulse">
          {message}
        </div>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <LoadingScreen message="Verifying Access" />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function ProtectedAdminPage({ children }) {
  return (
    <RequireAuth>
      <AdminLayout>{children}</AdminLayout>
    </RequireAuth>
  );
}

export default function App() {
  const currentHash = window.location.hash || '';
  if (currentHash.startsWith('#/register/')) {
    const slugPart = currentHash.substring(11).split('?')[0];
    return (
      <Router>
        <PublicLayout>
          <RegistrationForm forcedPublicSlug={slugPart} />
        </PublicLayout>
      </Router>
    );
  }

  return (
    <Router>
      <AuthProvider>
        <TenantProvider>
          <Routes>
            <Route
              path="/register/:publicSlug"
              element={
                <PublicLayout>
                  <RegistrationForm />
                </PublicLayout>
              }
            />
            <Route path="/login" element={<Login />} />

            <Route
              path="/dashboard"
              element={
                <ProtectedAdminPage>
                  <Dashboard />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedAdminPage>
                  <Tenants />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/tenant-users"
              element={
                <ProtectedAdminPage>
                  <TenantUsers />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/intake-groups"
              element={
                <ProtectedAdminPage>
                  <IntakeGroups />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/participants"
              element={
                <ProtectedAdminPage>
                  <Participants />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/email-templates"
              element={
                <ProtectedAdminPage>
                  <EmailTemplates />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/email-logs"
              element={
                <ProtectedAdminPage>
                  <EmailLogs />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/finances"
              element={
                <ProtectedAdminPage>
                  <Finances />
                </ProtectedAdminPage>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedAdminPage>
                  <SuperSettings />
                </ProtectedAdminPage>
              }
            />

            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </TenantProvider>
      </AuthProvider>
    </Router>
  );
}